import { useEffect } from 'react'
import { upcomingIcon, pastIcon } from '../lib/leaflet-icons.js'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-markercluster'
import EventMapPopup from './EventMapPopup.jsx'
import { isPastEvent } from '../lib/dateRange.js'

import { TILE_URL, TILE_ATTRIBUTION } from '../lib/basemap.js'

const DEFAULT_CENTER = [25, 0]
const DEFAULT_ZOOM = 2

function hasCoords(event) {
  return typeof event.lat === 'number' && typeof event.lng === 'number'
}

// #355: the map used to sit at a fixed world view (`center=[25,0] zoom=2`). At
// zoom 2 the whole world is 1024px tall, and the panel is 70vh — taller than
// that on any normal desktop — so the top of the panel was a flat grey band
// where there were simply no tiles to draw. It also meant a calendar filtered
// down to three European talks opened on a view of the Pacific.
//
// Fitting to the markers fixes both: the panel is full of map, and it is the
// map you wanted. `maxZoom` stops a single-marker result from diving to street
// level, and the refit is keyed on the marker set so it follows the filters.
function FitToMarkers({ points }) {
  const map = useMap()
  const key = points.map((p) => p.join(',')).join(';')
  useEffect(() => {
    if (!points.length) return
    map.fitBounds(points, { padding: [48, 48], maxZoom: 6, animate: false })
    // `key` is the real dependency — `points` is a fresh array every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, map])
  return null
}

export default function EventMap({ events }) {
  const plottable = (events || []).filter(hasCoords)

  return (
    <div className="overflow-hidden rounded-2xl ring-1 ring-slate-200/60 shadow-sm">
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        scrollWheelZoom
        /* #355: the grey band at the top of the panel was not a tile-loading
           failure — it was arithmetic. Tiles exist only between latitude ±85,
           so at zoom Z the whole world is 256 * 2^Z pixels tall. At zoom 2 that
           is 1024px, and a 70vh panel on a normal desktop is taller, so the
           excess had nothing to draw in it.

           Two clamps make it impossible rather than unlikely: the panel can
           never exceed 900px, and the map can never zoom out past 2 (1024px of
           world). Horizontal is already handled — Leaflet repeats tiles
           sideways, so only the vertical axis could ever run out. */
        minZoom={2}
        className="h-[60vh] min-h-[360px] max-h-[900px] w-full sm:h-[70vh]"
        worldCopyJump
      >
        <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
        <FitToMarkers points={plottable.map((e) => [e.lat, e.lng])} />
        <MarkerClusterGroup chunkedLoading showCoverageOnHover={false}>
          {/* #334: past markers are amber, upcoming ones brand-blue. The
              predicate lives in lib/dateRange so the map, the cards and the
              legend cannot disagree about what "past" means. */}
          {plottable.map((event) => (
            <Marker
              key={event.id}
              position={[event.lat, event.lng]}
              icon={isPastEvent({ startsAtUtc: event.endsAtUtc || event.startsAtUtc }) ? pastIcon : upcomingIcon}
            >
              <Popup>
                <EventMapPopup event={event} />
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>
      </MapContainer>
    </div>
  )
}

export { hasCoords }
